import * as React from 'react';

import { Tooltip, useMediaQuery } from '@mui/material';
import mediaQueries from 'mediaqueries';
import { useQuery } from '@tanstack/react-query';
import {
  fetchOutstandingFees,
  InsightsQueryKeys,
  OutstandingFeesType,
} from 'services/clients/insights';
import { PresetView } from 'pages/Shipments/Containers/types';
import { isFinite } from 'lodash';
import {
  centsToDollars,
  numericFormatter,
  percentageFormatterNoDecimal,
} from 'utils/container-transforms';
import { Routes } from 'constants/pages';
import { Permission, useHasPermission } from 'utils/permissions';
import { OutstandingFeesGraph } from '../OutstandingFeesGraph';
import { ScrollToTop } from '../../components/ScrollToTop';
import { AlertCard } from '../card-groups/AlertCard';
import { HistoricalDataLink } from './HistoricalDataLink';

export const statDollarTransformer = (value?: number | null): string => {
  return isFinite(value) ? centsToDollars(value as number, false) : '-';
};
export const statNumberTransformerNoDecimal = (value?: number | null): string => {
  return isFinite(value) ? numericFormatter.format(value as number) : '-';
};
export const statNumberTransformer = (value?: number | null): string => {
  return isFinite(value) ? (value as number).toFixed(2) : '-';
};
export const statPercentTransformer = (value?: number | null): string => {
  return isFinite(value) ? percentageFormatterNoDecimal.format(value as number) : '-';
};

export const OutstandingFees: React.FC = () => {
  const scrollableRef = React.useRef<HTMLDivElement>(null);
  const isTabletOrSmaller = useMediaQuery(`(max-width: ${mediaQueries.md})`);

  const outstandingQuery = useQuery({
    queryKey: [InsightsQueryKeys.OutstandingFees],
    queryFn: ({ signal }) => fetchOutstandingFees(signal),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  const userHasReportingPermission = useHasPermission([Permission.ShipmentWatchListReports]);

  const inner = React.useMemo(() => {
    return (
      <div
        className="grid grid-cols-1 space-y-5 gap-4 overflow-auto lg:space-y-0 lg:grid-cols-2 lg:gap-10"
        ref={scrollableRef}
      >
        <div className="space-y-8">
          <AlertCard
            title="outstanding demurrage"
            text="containers with demurrage to clear"
            tooltip="The estimated amount in dollars owed for containers that are currently at the terminal with unpaid demurrage fees."
            amount={outstandingQuery.data?.demurrage.amount || 0}
            containers={outstandingQuery.data?.demurrage.numberOfContainers || 0}
            presetView={PresetView.DemurrageDue}
            loading={outstandingQuery.isLoading}
            error={outstandingQuery.isError}
            stats={[
              {
                title: 'Avg. demurrage',
                value: statDollarTransformer(outstandingQuery.data?.demurrage.averageAmount),
              },
              {
                title: 'Avg. days in demurrage',
                value: statNumberTransformer(outstandingQuery.data?.demurrage.averageDays),
              },
            ]}
          />
          <OutstandingFeesGraph type={OutstandingFeesType.Demurrage} />
          <Tooltip title="Coming soon">
            <div>
              <HistoricalDataLink link="/" text="Access historical demurrage" disabled />
            </div>
          </Tooltip>
        </div>
        <div className="space-y-8">
          <AlertCard
            title="outstanding per diem"
            text="containers with per diem to clear"
            tooltip="The estimated amount in dollars owed for containers that have been gated out but not returned."
            amount={outstandingQuery.data?.perDiem.amount || 0}
            containers={outstandingQuery.data?.perDiem.numberOfContainers || 0}
            presetView={PresetView.OutstandingPerDiem}
            loading={outstandingQuery.isLoading}
            error={outstandingQuery.isError}
            stats={[
              {
                title: 'Dispute opportunity',
                value: statPercentTransformer(outstandingQuery.data?.perDiem.disputeOpportunity),
              },
              {
                title: 'Est. savings',
                value: statDollarTransformer(outstandingQuery.data?.perDiem.estimatedSavings),
                color: 'text-blue-700',
              },
              {
                title: 'Avg. per diem',
                value: statDollarTransformer(outstandingQuery.data?.perDiem.averageAmount),
              },
              {
                title: 'Avg. days in per diem',
                value: statNumberTransformer(outstandingQuery.data?.perDiem.averageDays),
              },
            ]}
          />
          <OutstandingFeesGraph type={OutstandingFeesType.PerDiem} />
          {userHasReportingPermission ? (
            <HistoricalDataLink link={Routes.ShipmentsReports} text="Access historical per diem" />
          ) : (
            <HistoricalDataLink link={Routes.Plans} text="Upgrade to access historical per diem" />
          )}
        </div>
        {isTabletOrSmaller && <ScrollToTop element={scrollableRef} className="mb-12" />}
      </div>
    );
  }, [isTabletOrSmaller, outstandingQuery, userHasReportingPermission]);

  return <div className="space-y-4">{inner}</div>;
};
OutstandingFees.displayName = 'Shipments.Dashboard.OutstandingFees';
